import { UnorderedListOutlined } from '@ant-design/icons';
import { orderBy } from 'lodash';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';

import {
  type FloatingCardState,
  type ResizeCorner,
  FLOATING_CARD_BELOW_MODAL_Z_INDEX,
  FloatingCardShell,
  getAnchorPosition,
  getFloatingCardBounds,
  useFloatingCard,
} from 'components/FloatingCard';
import { Button } from 'components/ui';
import { useIsInView } from 'utils/hooks';

import { propagationTypeMapping } from '../../utils';
import { ColorCodingAttribute } from '../BladeDamagePlotContainer';
import { type ColorCodingMap, NullShapeComponent } from '../pointUtils';

import {
  DamageCountSpan,
  LegendContent,
  LegendScrollArea,
  LegendSymbol,
  LegendToggleWrapper,
} from './ColorCodingLegend.style';
import { getLegendInitialSize, getLegendMaxHeight, getLegendScale, LEGEND_MIN_SIZE } from './utils';

type LegendSession = {
  floatingState?: FloatingCardState;
  userPositioned: boolean;
  userResized: boolean;
};

type ColorCodingLegendCardProps = {
  colorCodingAttribute: ColorCodingAttribute;
  colorCodingMap: ColorCodingMap;
  totalDamageCount: number;
  toggleRef: React.RefObject<HTMLButtonElement | null>;
  boundsRef: React.RefObject<HTMLElement | null>;
  legendSessionRef: React.MutableRefObject<LegendSession>;
  onClose: () => void;
};

const ColorCodingLegendCard = ({
  colorCodingAttribute,
  colorCodingMap,
  totalDamageCount,
  toggleRef,
  boundsRef,
  legendSessionRef,
  onClose,
}: ColorCodingLegendCardProps) => {
  const theme = useTheme();
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const contentMeasureRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [enableScroll, setEnableScroll] = useState(false);
  const isScrolledToTop: boolean = useIsInView(topRef);
  const isScrolledToBottom: boolean = useIsInView(bottomRef);

  const initialSize = useMemo(() => getLegendInitialSize(colorCodingMap), [colorCodingMap]);

  const {
    position,
    size,
    setSize,
    setPosition,
    headerRef,
    isAnchorPositioned,
    onHeaderMouseDown,
    onResizeMouseDown,
    resizeCursors,
    getCornerHandleProps,
  } = useFloatingCard({
    minSize: LEGEND_MIN_SIZE,
    initialSize,
    initialState: legendSessionRef.current.floatingState,
    anchorRef: toggleRef,
    anchorPlacement: 'above',
    boundsRef,
    onUnmount: state => {
      legendSessionRef.current.floatingState = state;
    },
  });

  useEffect(() => {
    if (legendSessionRef.current.userPositioned || legendSessionRef.current.userResized) {
      legendSessionRef.current.floatingState = { position, size };
    }
  }, [legendSessionRef, position, size]);

  const legendScale = getLegendScale(size.width);

  const noValueDamageCount: number =
    totalDamageCount -
    Object.values(colorCodingMap).reduce((acc, { damageCount }) => acc + (damageCount ?? 0), 0);

  const handleHeaderMouseDown = useCallback(
    (event: React.MouseEvent) => {
      legendSessionRef.current.userPositioned = true;
      onHeaderMouseDown(event);
    },
    [legendSessionRef, onHeaderMouseDown]
  );

  const handleResizeMouseDown = useCallback(
    (event: React.MouseEvent, corner: ResizeCorner) => {
      legendSessionRef.current.userResized = true;
      setEnableScroll(true);
      onResizeMouseDown(event, corner);
    },
    [legendSessionRef, onResizeMouseDown]
  );

  const syncLegendAnchorPosition = useCallback(
    (cardWidth: number, cardHeight: number) => {
      const { userPositioned, userResized } = legendSessionRef.current;
      if (userPositioned || userResized || !toggleRef.current) {
        return;
      }

      setPosition(
        getAnchorPosition(
          toggleRef.current,
          cardWidth,
          cardHeight,
          'above',
          getFloatingCardBounds(boundsRef)
        )
      );
    },
    [boundsRef, legendSessionRef, setPosition, toggleRef]
  );

  const applyLegendHeight = useCallback(
    (targetHeight: number) => {
      setSize(currentSize => {
        if (currentSize.height === targetHeight) {
          return currentSize;
        }

        syncLegendAnchorPosition(currentSize.width, targetHeight);
        return { ...currentSize, height: targetHeight };
      });
    },
    [setSize, syncLegendAnchorPosition]
  );

  useLayoutEffect(() => {
    if (!isAnchorPositioned || legendSessionRef.current.userResized) {
      return;
    }

    const headerHeight = headerRef.current?.offsetHeight ?? 0;
    const contentHeight = contentMeasureRef.current?.offsetHeight ?? 0;
    if (!contentHeight) {
      return;
    }

    const maxHeight = getLegendMaxHeight();
    const naturalHeight = headerHeight + contentHeight;
    const isHeightCapped = naturalHeight > maxHeight;
    const targetHeight = Math.min(Math.max(LEGEND_MIN_SIZE.height, naturalHeight), maxHeight);

    setEnableScroll(isHeightCapped);
    applyLegendHeight(targetHeight);

    if (!isHeightCapped) {
      requestAnimationFrame(() => {
        const scrollEl = scrollAreaRef.current;
        if (!scrollEl || legendSessionRef.current.userResized) {
          return;
        }

        const overflow = scrollEl.scrollHeight - scrollEl.clientHeight;
        if (overflow > 0) {
          setSize(currentSize => {
            const nextHeight = currentSize.height + overflow;
            syncLegendAnchorPosition(currentSize.width, nextHeight);
            return { ...currentSize, height: nextHeight };
          });
        }
      });
    }
  }, [
    applyLegendHeight,
    colorCodingMap,
    isAnchorPositioned,
    headerRef,
    legendScale,
    noValueDamageCount,
    setSize,
    legendSessionRef,
    syncLegendAnchorPosition,
    totalDamageCount,
  ]);

  if (!isAnchorPositioned) {
    return null;
  }

  return (
    <FloatingCardShell
      title={`${colorCodingAttribute}`}
      icon={<UnorderedListOutlined />}
      onClose={onClose}
      scale={legendScale}
      position={position}
      size={size}
      headerRef={headerRef}
      onHeaderMouseDown={handleHeaderMouseDown}
      onResizeMouseDown={handleResizeMouseDown}
      resizeCursors={resizeCursors}
      getCornerHandleProps={getCornerHandleProps}
      usePortal
      zIndex={FLOATING_CARD_BELOW_MODAL_Z_INDEX}
      testId="color-coding-legend"
    >
      <LegendScrollArea
        ref={scrollAreaRef}
        $enableScroll={enableScroll}
        $isScrolledToTop={isScrolledToTop}
        $isScrolledToBottom={isScrolledToBottom}
      >
        <LegendContent ref={contentMeasureRef}>
          <div ref={topRef} />
          <ul>
            {colorCodingMap &&
              orderBy(
                Object.entries(colorCodingMap),
                [0],
                [
                  [ColorCodingAttribute.Severity, ColorCodingAttribute.OpSeverity].includes(
                    colorCodingAttribute
                  )
                    ? 'desc'
                    : 'asc',
                ]
              ).map(
                (
                  [
                    value,
                    {
                      symbol: { ShapeComponent, color },
                      damageCount,
                    },
                  ],
                  i
                ) =>
                  !!damageCount && (
                    <li key={i}>
                      <LegendSymbol
                        viewBox="0 0 10 10"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <ShapeComponent
                          x={5}
                          y={5}
                          color={color}
                          opacity={theme.plot.opacity.default}
                        />
                      </LegendSymbol>
                      <span>
                        {[ColorCodingAttribute.Severity, ColorCodingAttribute.OpSeverity].includes(
                          colorCodingAttribute
                        )
                          ? colorCodingAttribute
                          : ''}{' '}
                        {colorCodingAttribute === ColorCodingAttribute.PropagationType
                          ? propagationTypeMapping[value]
                          : value}
                      </span>
                      : <DamageCountSpan>{damageCount}</DamageCountSpan>
                    </li>
                  )
              )}
            <li>
              <LegendSymbol viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                <NullShapeComponent
                  x={5}
                  y={5}
                  color={theme.plot.defaultPointColor}
                  opacity={theme.plot.opacity.default}
                />
              </LegendSymbol>
              <span> No {colorCodingAttribute}</span>:{' '}
              <DamageCountSpan>{noValueDamageCount}</DamageCountSpan>
            </li>
          </ul>
          <div ref={bottomRef} />
        </LegendContent>
      </LegendScrollArea>
    </FloatingCardShell>
  );
};

type ColorCodingLegendProps = {
  colorCodingAttribute: ColorCodingAttribute;
  colorCodingMap: ColorCodingMap;
  totalDamageCount: number;
  boundsRef: React.RefObject<HTMLElement | null>;
};

export const ColorCodingLegend: React.FunctionComponent<ColorCodingLegendProps> = ({
  colorCodingAttribute,
  colorCodingMap,
  totalDamageCount,
  boundsRef,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const legendSessionRef = useRef<LegendSession>({
    userPositioned: false,
    userResized: false,
  });

  return (
    <LegendToggleWrapper>
      <Button
        ref={toggleRef}
        icon={<UnorderedListOutlined />}
        onClick={() => setIsOpen(open => !open)}
        title={isOpen ? 'Hide legend' : 'Show legend'}
      />
      {isOpen && (
        <ColorCodingLegendCard
          colorCodingAttribute={colorCodingAttribute}
          colorCodingMap={colorCodingMap}
          totalDamageCount={totalDamageCount}
          toggleRef={toggleRef}
          boundsRef={boundsRef}
          legendSessionRef={legendSessionRef}
          onClose={() => setIsOpen(false)}
        />
      )}
    </LegendToggleWrapper>
  );
};
